import * as React from "react";
import { cn } from "../../lib/utils";
import { TESTIMONIALS_SECTION_STYLES } from "./constants";
import type { TestimonialsSectionProps } from "./types";

function renderStars(rating: number, max: number) {
  const filled = Math.round(rating);
  return Array.from({ length: max }, (_, idx) => {
    const isActive = idx < filled;
    return (
      <span
        key={idx}
        aria-hidden="true"
        className={cn(
          TESTIMONIALS_SECTION_STYLES.star,
          isActive
            ? TESTIMONIALS_SECTION_STYLES.starActive
            : TESTIMONIALS_SECTION_STYLES.starInactive
        )}
      >
        ★
      </span>
    );
  });
}

export const TestimonialsSection = React.forwardRef<
  HTMLElement,
  TestimonialsSectionProps
>(
  (
    {
      className,
      title,
      description,
      testimonials,
      maxRating = 5,
      ...props
    },
    ref
  ) => {
    return (
      <section
        ref={ref}
        className={cn(TESTIMONIALS_SECTION_STYLES.root, className)}
        {...props}
      >
        <h2 className={TESTIMONIALS_SECTION_STYLES.title}>{title}</h2>
        {description && (
          <p className={TESTIMONIALS_SECTION_STYLES.description}>
            {description}
          </p>
        )}

        <ul className={TESTIMONIALS_SECTION_STYLES.grid}>
          {testimonials.map((testimonial) => (
            <li key={testimonial.id} className={TESTIMONIALS_SECTION_STYLES.card}>
              <div
                className={TESTIMONIALS_SECTION_STYLES.stars}
                aria-label={`${testimonial.rating} of ${maxRating} stars`}
                role="img"
              >
                {renderStars(testimonial.rating, maxRating)}
              </div>

              <blockquote className={TESTIMONIALS_SECTION_STYLES.quote}>
                &ldquo;{testimonial.quote}&rdquo;
              </blockquote>

              <div className={TESTIMONIALS_SECTION_STYLES.author}>
                {testimonial.avatarUrl && (
                  <img
                    src={testimonial.avatarUrl}
                    alt={testimonial.author}
                    className={TESTIMONIALS_SECTION_STYLES.avatar}
                    loading="lazy"
            decoding="async"
                  />
                )}
                <div className={TESTIMONIALS_SECTION_STYLES.authorInfo}>
                  <span className={TESTIMONIALS_SECTION_STYLES.authorName}>
                    {testimonial.author}
                  </span>
                  {testimonial.role && (
                    <span className={TESTIMONIALS_SECTION_STYLES.authorRole}>
                      {testimonial.role}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    );
  }
);

TestimonialsSection.displayName = "TestimonialsSection";
